import nearWorldClock from '@flipster/utils/clock/near-world-clock';
import { websocketLogger } from '@flipster/utils/datadog';

/** Datadog emit interval. Independent of the outdated-message clock skew threshold. */
export const reconnectLogThrottleWindowMs = 5000;

export type SocketReconnectReason = 'outdated_message' | 'ping_timeout';

export interface LogSocketReconnectParams {
  socket_name: string;
  reason: SocketReconnectReason;
  extra?: Record<string, unknown>;
}

interface ThrottleState {
  lastEmittedAt: number;
  lastSeenAt: number;
  suppressedCount: number;
}

const throttleState = new Map<string, ThrottleState>();

function throttleKey(socketName: string, reason: SocketReconnectReason): string {
  return `${socketName}:${reason}`;
}

/** @internal test only */
export function resetSocketReconnectLogThrottle(): void {
  throttleState.clear();
}

export function logSocketReconnect({ socket_name, reason, extra }: LogSocketReconnectParams): void {
  const key = throttleKey(socket_name, reason);
  const now = Date.now();
  const state = throttleState.get(key);

  if (state && now - state.lastEmittedAt < reconnectLogThrottleWindowMs) {
    state.suppressedCount += 1;
    state.lastSeenAt = now;
    return;
  }

  const isSameBurst = state != null && now - state.lastSeenAt <= reconnectLogThrottleWindowMs;
  const suppressed_count = isSameBurst ? state.suppressedCount : 0;
  throttleState.set(key, { lastEmittedAt: now, lastSeenAt: now, suppressedCount: 0 });

  websocketLogger.warn('websocket_reconnect', {
    ...extra,
    reason,
    socket_name,
    clock_synced: nearWorldClock.synced,
    clock_gap_ms: nearWorldClock.gapMilliSecondsFromSystemTime,
    suppressed_count,
  });
}
